import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { getSelectedCountry } from '../pages/countrySelector.utils';

export function extractTwoLetterCultureFromPath(path: string): string | null {
    const firstSegment = path.trim().split('/').filter(Boolean)[0];
    if (!firstSegment) return null;
    const culture = firstSegment.toLowerCase();
    return /^[a-z]{2}$/.test(culture) ? culture : null;
}

export function extractTwoLetterCultureFromCookie(value: string | null): string | null {
    if (!value) return null;
    const match = value.trim().match(/^\/?([a-z]{2})(?:\/|$)/i);
    const culture = match?.[1];
    return culture ? culture.toLowerCase() : null;
}
/**
 * Composable to extract and work with culture codes from the URL.
 * Expects URLs in format: /[culture-code]/... (e.g., /da/home, /se/products)
 */
export function useCulture() {
    let route: ReturnType<typeof useRoute> | undefined;
    try {
        route = useRoute();
    } catch {
        route = undefined;
    }
    const routeCultureCode = computed(() => {
        const currentPath = route?.path ?? (typeof window !== 'undefined' ? window.location?.pathname : undefined) ?? '';
        return extractTwoLetterCultureFromPath(currentPath) ?? '';
    });

    /**
     * Extracts the culture code from the current route path, or falls back to the cookie.
     * Returns the 2-letter culture code if found, otherwise returns empty string.
        * Note: keeping the cookie in sync with the URL is handled globally by the router.
     * @example
    * // Cookie: selectedCountry='da' (or legacy '/da/') -> cultureCode 'da'
     * // URL: domain.com/da/home -> cultureCode 'da'
     * // URL: domain.com/se/products -> cultureCode 'se'
     * // URL: domain.com/ -> cultureCode ''
     */
    const cultureCode = computed(() => {
        if (routeCultureCode.value) {
            return routeCultureCode.value;
        }

        const cookieValue = getSelectedCountry();
        return extractTwoLetterCultureFromCookie(cookieValue) ?? '';
    });

    /**
     * Generates a culture-aware path by prepending the culture code to the given path.
     * @param path - The path to prepend the culture code to (e.g., '/', '/products')
     * @returns Culture-aware path (e.g., '/da/', '/da/products') or original path if no culture
     */
    const getCulturePath = (path: string) => {
        if (!cultureCode.value) {
            return path;
        }
        // Ensure path starts with /
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;

        // If path is just '/', return '/[culture]/'
        if (normalizedPath === '/') {
            return `/${cultureCode.value}/`;
        }
        // Prepend culture code

        return `/${cultureCode.value}${normalizedPath}`;
    };
    return {
        cultureCode,
        getCulturePath,
    };
}

